import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomChip from '@ui-components/Chip/CustomChip';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import FilterIcon from '@assets/images/Filter.svg';
import SearchIcon from '@assets/images/search.svg';
import { getEndItem, getSortIcon, getStartItem } from '../components/Sorting';
import useDebouncing from '../components/useDebouncing';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomPagination from '@ui-components/Table/CustomPagination';
import { useGetConsultsList } from '../../API/hooks/ConsultsHook';
import { rowsPerPage } from '../constants';
import EditOnlyIcon from '@assets/images/editOnly.svg';
import CancelIcon from '@assets/images/cancel_consult.svg';
import { useNavigate } from 'react-router';
import {
  CONSULTS_MORE_DETAILS_ROUTE_PATH,
  CONSULTS_ROUTE_PATH,
  NEW_CONSULTS_ROUTE_PATH,
} from '../../routers/routePaths';
import type {
  PartnerFacilitiesFilter,
  SelectedState,
} from '../../API/services/PartnerFacilityFilterService';
import { useGetPartnerFacilityFilterData } from '../../API/hooks/PartnerFacilityFilterHook';
import CustomPopover from '@ui-components/Popover/CustomPopover';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import PartnerFacilitySelector from '../components/PartnerFacilitySelector';
import { StatusFilterSelector } from '../components/StatusFilterSelector';
import { FollowUpFilterSelector } from '../components/FollowUpFilterSelector';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';
import './consult.css';
import ChatIcon from '@assets/images/chat.svg';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import { formatFieldValue } from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import { useFilters } from '../../routers/FilterContext';
import { CallStatusEnum } from '../../shared/CallStatusEnum';
import CancelConsultModel, {
  type CancelReasonFacilitySubmitParams,
  type CancelReasonFacilityParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import {
  useCancelConsultMetadata,
  useSaveCancelConsult,
} from '../../shared/statusRequest/StatusRequestSetup';

const ConsultsList = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { filterContext, setFilterContext, getFilterCounts } = useFilters();
  const query = new URLSearchParams(location.search);
  const toast = query.get('toast');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTermConsults, setSearchTermConsults] = useState<string>(
    filterContext.searchedValue
  );
  const debouncedSearchTerm = useDebouncing(searchTermConsults, 500);

  const [page, setPage] = useState(0);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<'asc' | 'desc' | undefined>(
    undefined
  );

  const [partnerFacilityFilters, setPartnerFacilityFilters] = useState<
    PartnerFacilitiesFilter | undefined
  >();
  const {
    PartnerFacilityFilterData,
    // isLoading: isLoadingFilters,
    refetch: refetchFilters,
  } = useGetPartnerFacilityFilterData();

  const [selectedFacilities, setSelectedFacilities] = useState(
    filterContext.facilities
  );

  const [selectedStatusId, setSelectedStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [filterStatusId, setFilterStatusId] = useState<number[]>(
    filterContext.statusIds
  );
  const [selectedFollowUpId, setSelectedFollowUpId] = useState<number[]>(
    filterContext.followUpIds ?? []
  );
  const [filterFollowUpId, setFilterFollowUpId] = useState<number[]>(
    filterContext.followUpIds ?? []
  );
  const [selectedConsultId, setSelectedConsultId] = useState<number>(0);
  const [openCancelConsult, setOpenCancelConsult] = useState(false);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<
    number[] | undefined
  >([]);
  const [facilityFilterSearch, setFacilityFilterSearch] = useState<string>('');

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearchTerm,
    selectedStatusId,
    selectedFacilityIds,
    selectedFollowUpId,
  ]);

  useEffect(() => {
    setSelectedFacilities(filterContext.facilities);
    const filterIds = Object.values(selectedFacilities).flat();
    // convert string → number

    setSelectedFacilityIds(filterIds);
    setFilterStatusId(filterContext.statusIds);
    setFilterFollowUpId(filterContext.followUpIds ?? []);
    setSelectedFollowUpId(filterContext.followUpIds ?? []);
    setSearchTermConsults(filterContext.searchedValue);
  }, [filterContext]);

  const { ConsultsListResponse, isLoading } = useGetConsultsList({
    userId: userId,
    page,
    size: rowsPerPage,
    sortField,
    sortType,
    searchTerm: debouncedSearchTerm,
    statusId: selectedStatusId,
    facilityId: selectedFacilityIds,
    followUpId: selectedFollowUpId,
  });

  const {
    CancelConsultMetadata: ConsultCancelMetaData,
    refetch: refetchCancelMetaData,
  } = useCancelConsultMetadata(PORTAL_TYPES.OPERATIONS);

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (toast === 'success') {
      setMessage('Consult saved successfully!');
      setOpenToast(true);
    } else if (toast === 'update') {
      setMessage('Consult updated successfully!');
      setOpenToast(true);
    }
    navigate(CONSULTS_ROUTE_PATH, { replace: true });
  }, [toast, navigate]);

  const { mutate: cancelConsult } = useSaveCancelConsult();

  const ConsultsListCount = ConsultsListResponse?.totalRecords ?? 0;

  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field && sortType === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortType(direction);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage - 1);
  };

  useEffect(() => {
    if (PartnerFacilityFilterData) {
      setPartnerFacilityFilters(PartnerFacilityFilterData);
    }
  }, [PartnerFacilityFilterData]);

  const handleConsultsFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    refetchFilters();
  };
  const handleConsultsSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/^\s+/, '');
    value = value.replaceAll(/\s{2,}/g, ' ');
    setSearchTermConsults(value);
    setFilterContext({
      ...filterContext,
      searchedValue: value,
    });
  };

  const handleEditConsultsInfo = (consultId: number) => {
    navigate(NEW_CONSULTS_ROUTE_PATH + `/${consultId}`, {
      replace: true,
      state: { isConsultList: true, consultId: consultId },
    });
  };
  const handleApplyFilters = () => {
    setSelectedStatusId(filterStatusId);
    setSelectedFollowUpId(filterFollowUpId);
    const filterIds = Object.values(selectedFacilities).flat();
    // convert string → number

    setSelectedFacilityIds(filterIds);
    setFilterContext({
      ...filterContext,
      facilities: selectedFacilities,
      statusIds: filterStatusId,
      followUpIds: filterFollowUpId,
    });
  };
  const handleFacilityFilterSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;
    value = value.replace(/^\s+/, '');
    value = value.replaceAll(/\s{2,}/g, ' ');

    const result = searchPartnerFacilities(PartnerFacilityFilterData, value);

    setPartnerFacilityFilters(result as PartnerFacilitiesFilter);

    setFacilityFilterSearch(value);
  };
  const searchPartnerFacilities = (
    data: PartnerFacilitiesFilter | undefined,
    searchText: string
  ) => {
    if (!searchText?.trim()) return data;

    const query = searchText.toLowerCase();

    const filteredPartnerFacilitiesMap = data?.partnerFacilitiesMap
      .map((partner) => {
        const partnerMatch = partner.partnerName.toLowerCase().includes(query);

        const matchedFacilities = partner.facilities.filter((facility) =>
          facility.name.toLowerCase().includes(query)
        );

        if (partnerMatch) {
          return { ...partner }; // keep all facilities
        }

        if (matchedFacilities.length > 0) {
          return {
            ...partner,
            facilities: matchedFacilities,
          };
        }

        return null;
      })
      .filter(Boolean);

    return {
      ...data,
      partnerFacilitiesMap: filteredPartnerFacilitiesMap,
    };
  };
  const handleStatusFilter = (statusIds: number[]) => {
    setFilterStatusId(statusIds);
  };

  const handleFollowUpFilter = (followUpIds: number[]) => {
    setFilterFollowUpId(followUpIds);
  };

  const handleFacilityChange = (value: SelectedState) => {
    setSelectedFacilities(value);
  };

  const handleViewConsultsInfo = (consultId: number) => {
    navigate(CONSULTS_MORE_DETAILS_ROUTE_PATH + `/${consultId}`);
  };

  const handleOpenConsultCancel = (consultId: number) => {
    refetchCancelMetaData();
    setOpenCancelConsult(true);
    setSelectedConsultId(consultId);
  };

  const handleSubmitConsultCancelReason = async (
    cancelReasonData: CancelReasonFacilityParams
  ) => {
    const mappedTechnicalDifficultiesId =
      [
        cancelReasonData.patientAdmittedId,
        cancelReasonData.noLongerRequiredId,
        cancelReasonData.patientDischargedId,
        cancelReasonData.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: cancelReasonData.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: cancelReasonData.otherReason,
    };

    cancelConsult(
      {
        userId: userId.toString(),
        portalType: PORTAL_TYPES.OPERATIONS,
        consultId: selectedConsultId,
        ...payload,
      },
      {
        onSuccess: () => {
          setOpenCancelConsult(false);
        },
      }
    );
  };

  const handleClose_SnackBar = () => {
    setOpenToast(false);
  };

  const handleConsultSpecificChat = (consultId: number) => {
    navigate(CONSULTS_MORE_DETAILS_ROUTE_PATH + `/${consultId}`, {
      state: { chatOpen: true },
    });
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="toast-consult-list"
          successOrError={'success'}
          open={openToast}
          handleClose={handleClose_SnackBar}
          toastrMessage={message}
        />
      )}

      {openCancelConsult && (
        <CancelConsultModel
          openCancelConsultPopup={openCancelConsult}
          onCloseCancelConsultPopup={() => setOpenCancelConsult(false)}
          ConsultCancelMetaData={ConsultCancelMetaData ?? undefined}
          onSubmitCancelConsult={(values) => {
            handleSubmitConsultCancelReason(values);
            setOpenCancelConsult(false);
          }}
          portal={PORTAL_TYPES.OPERATIONS}
        />
      )}
      <Box className="pt-20">
        <Grid2 container className="align-items-center">
          <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
            <CustomTypography
              id="page-title-consults"
              variant="h4"
              className="text-align-left"
            >
              Consults
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
            <Box className="flex float-right">
              <Box>
                <CustomTextField
                  id="txt-consults-search"
                  name="search-consults"
                  placeholder="Search Consults"
                  size="small"
                  type="text"
                  value={searchTermConsults}
                  onChange={handleConsultsSearch}
                  startAdornment={
                    <CustomIconButton
                      id="search-icon"
                      size="small"
                      icon={<SearchIcon />}
                    />
                  }
                />
              </Box>
              <Box className="ml-20">
                <CustomBadge
                  id={`notification`}
                  badgeContent={getFilterCounts(filterContext).totalCount}
                  color="error"
                  max={99}
                >
                  <CustomChip
                    aria-haspopup="true"
                    onClick={(e) => {
                      handleConsultsFilterClick(e);
                    }}
                    className="box-border2"
                    id="demo-chip"
                    label="Filters"
                    variant="outlined"
                    icon={<FilterIcon />}
                    iconPosition="left"
                  />
                </CustomBadge>
              </Box>
              <Box className="ml-20">
                <CustomButton
                  variant="contained"
                  id="btn-add-consult"
                  color="primary"
                  size="large"
                  onClick={() => {
                    navigate(NEW_CONSULTS_ROUTE_PATH, {
                      replace: true,
                    });
                  }}
                >
                  Add Consult
                </CustomButton>
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box className="pt-20">
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CustomTypography
              id="loading-text"
              variant="h5"
              className="text-align-center pt-32"
              color="textDisabled"
            >
              Loading...
            </CustomTypography>
          </Box>
        ) : (
          <CustomTable id="table-consults">
            <CustomTableHead id="table-head-consults">
              <CustomTableRow id="consults-header">
                <CustomTableCell
                  id="table-cell-consults-id"
                  onClick={() => handleSort('sessionId')}
                >
                  SESSION ID&nbsp;
                  {sortField === 'sessionId' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-created-time"
                  onClick={() => handleSort('createdTime')}
                >
                  CREATED ON&nbsp;
                  {sortField === 'createdTime' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-total-hold-time"
                  onClick={() => handleSort('totalHoldTimeSeconds')}
                >
                  TOTAL WAIT TIME&nbsp;
                  {sortField === 'totalHoldTimeSeconds' &&
                    getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-patient-last-name"
                  onClick={() => handleSort('patientLastName')}
                >
                  CAL. WAIT TIME&nbsp;
                  {sortField === 'patientLastName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-EDorIP"
                  onClick={() => handleSort('emergencyDeptOrInPatient')}
                >
                  ED/IP&nbsp;
                  {sortField === 'emergencyDeptOrInPatient' &&
                    getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-facility-name"
                  onClick={() => handleSort('facilityName')}
                >
                  FACILITY NAME&nbsp;
                  {sortField === 'facilityName' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-patient-first-name"
                  onClick={() => handleSort('patientFirstName')}
                >
                  PATIENT NAME &nbsp;
                  {sortField === 'patientFirstName' && getSortIcon(sortType)}
                </CustomTableCell>

                <CustomTableCell
                  id="table-cell-date-of-birth"
                  onClick={() => handleSort('dateOfBirth')}
                >
                  DOB &nbsp;
                  {sortField === 'dateOfBirth' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-mrn"
                  onClick={() => handleSort('mrn')}
                >
                  MRN &nbsp;
                  {sortField === 'mrn' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-followUp"
                  onClick={() => handleSort('followUp')}
                >
                  FOLLOW-UP &nbsp;
                  {sortField === 'followUp' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-assigned-clinician"
                  onClick={() => handleSort('assignedClinician')}
                >
                  ASSIGNED CLINICIAN &nbsp;
                  {sortField === 'assignedClinician' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell
                  id="table-cell-consult-status"
                  onClick={() => handleSort('status')}
                >
                  STATUS &nbsp;
                  {sortField === 'status' && getSortIcon(sortType)}
                </CustomTableCell>
                <CustomTableCell id="table-cell-facility-action">
                  ACTIONS &nbsp;
                </CustomTableCell>
              </CustomTableRow>
            </CustomTableHead>
            {ConsultsListResponse &&
            ConsultsListResponse?.consultsList.length > 0 ? (
              <CustomTableBody id="table-body-consults">
                {ConsultsListResponse?.consultsList.map((consults) => (
                  <CustomTableRow
                    key={consults.id}
                    id={`table-row-consults-${consults.id}`}
                  >
                    <CustomTableCell
                      id={`table-cell-consults-id-${consults.id}`}
                    >
                      <CustomLink
                        id={`link-consult-id-${consults.id}`}
                        color="primary"
                        onClick={() => handleViewConsultsInfo(consults.id)}
                      >
                        {consults.sessionId || 'N/A'}
                      </CustomLink>
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-created-${consults.id}`}
                    >
                      {formatFieldValue(consults.createdTime)}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-total-wait-time-${consults.id}`}
                    >
                      {consults.totalWaitTime || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-calculated-wait-time-${consults.id}`}
                    >
                      {consults.calculatedWaitTime || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-EDorIP-${consults.id}`}
                    >
                      {consults.emergencyDeptOrInPatient || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-facility-name-${consults.id}`}
                    >
                      {consults.facilityName || 'N/A'}
                    </CustomTableCell>

                    <CustomTableCell
                      id={`table-cell-consults-patient-first-name-${consults.id}`}
                    >
                      {consults.patientFirstName
                        ? `${consults.patientFirstName} ${consults.patientLastName}`
                        : 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-date-of-birth-${consults.id}`}
                    >
                      {formatFieldValue(consults.dateOfBirth, {
                        isDateOfBirth: true,
                      })}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-mrn-${consults.id}`}
                    >
                      {consults.patientMrn || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-followUp-${consults.id}`}
                    >
                      {consults.followUp || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-assigned-clinician-${consults.id}`}
                    >
                      {consults.assignedClinician || 'N/A'}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-status-${consults.id}`}
                    >
                      {consults.status}
                    </CustomTableCell>
                    <CustomTableCell
                      id={`table-cell-consults-action-${consults.id}`}
                      className="width-5"
                    >
                      <Box className="flex align-items-center">
                        <Box>
                          <CustomIconButton
                            id="edit-icon"
                            size="small"
                            icon={<EditOnlyIcon />}
                            onClick={() => {
                              handleEditConsultsInfo(consults.id);
                            }}
                          />
                        </Box>
                        <Box className="ml-12">
                          <CustomBadge
                            id={`${consults.id}-chat-notification`}
                            badgeContent={consults.unreadMessageCount}
                            color="error"
                            max={99}
                          >
                            <CustomIconButton
                              id="chat-icon"
                              size="small"
                              icon={<ChatIcon />}
                              onClick={() =>
                                handleConsultSpecificChat(consults.id)
                              }
                            />
                          </CustomBadge>
                        </Box>
                        {[
                          CallStatusEnum.New,
                          CallStatusEnum.InProgress,
                          CallStatusEnum.CallStarted,
                          CallStatusEnum.OnHold,
                        ].includes(
                          consults.status.toLowerCase() as CallStatusEnum
                        ) && (
                          <Box className="ml-12">
                            <CustomIconButton
                              id="cancel-icon"
                              size="small"
                              icon={<CancelIcon />}
                              onClick={() => {
                                handleOpenConsultCancel(consults.id);
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </CustomTableCell>
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            ) : (
              <CustomTableBody id="table-body-consults">
                <CustomTableRow id="table-row-consults">
                  <CustomTableCell id="table-cell-consults-id" colspan={10}>
                    <Box className="flex flex-justify-center align-items-center">
                      <CustomTypography
                        id="no-consults-found"
                        variant="body2"
                        color="textDisabled"
                      >
                        No Consults Found
                      </CustomTypography>
                    </Box>
                  </CustomTableCell>
                </CustomTableRow>
              </CustomTableBody>
            )}
          </CustomTable>
        )}

        {ConsultsListCount > 0 && (
          <Box className="mt-32">
            <Grid2 container className="align-items-center">
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <Box className="flex justify-content-start">
                  <CustomTypography
                    id="showing-row-count-text"
                    variant="body2"
                    color="textDisabled"
                  >
                    Showing {getStartItem(page, rowsPerPage)} -{' '}
                    {getEndItem(page, rowsPerPage, ConsultsListCount)} of{' '}
                    {ConsultsListCount} Consults
                  </CustomTypography>
                </Box>
              </Grid2>
              {ConsultsListResponse &&
                ConsultsListResponse.totalRecords > rowsPerPage && (
                  <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                    <Box display="flex" justifyContent="end">
                      <CustomPagination
                        id="consults-list-pagination"
                        count={Math.ceil(ConsultsListCount / rowsPerPage)}
                        page={page + 1}
                        onPageChange={handlePageChange}
                        color="standard"
                      />
                    </Box>
                  </Grid2>
                )}
            </Grid2>
          </Box>
        )}
      </Box>
      <Box>
        <CustomPopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="mt-4"
        >
          <Box className="p-24 width-500px">
            <Box className="text-align-left">
              <CustomTypography
                id="intake-form-filter-title"
                variant="h4"
                color="textPrimary"
              >
                Filters
              </CustomTypography>
              <Box className="pt-8">
                <CustomTypography
                  id="intake-form-filter-subtitle"
                  variant="body2"
                  color="textDisabled"
                >
                  Filter records by facility and status
                </CustomTypography>
              </Box>
            </Box>
            <Box className="pt-16">
              <CustomDivider />
            </Box>
            <Box className="flex flex-column">
              <Grid2
                container
                justifyContent="center"
                spacing={2}
                className="width-100"
              >
                <Grid2 size={{ xs: 6 }}>
                  <Box className="flex-between-center pt-24">
                    <Box>
                      <CustomTypography
                        id="intake-form-facility-mapping-title"
                        variant="body2"
                        color="textDisabled"
                      >
                        FACILITIES
                      </CustomTypography>
                    </Box>
                    <Box></Box>
                  </Box>
                  <Box className="pt-20 pb-20">
                    <CustomTextField
                      id="intake-form-facility-search"
                      variant="outlined"
                      size="small"
                      value={facilityFilterSearch}
                      placeholder="Search facilities..."
                      startAdornment={
                        <CustomIconButton
                          id="search-icon"
                          size="small"
                          icon={<SearchIcon />}
                        />
                      }
                      onChange={handleFacilityFilterSearch}
                    />
                  </Box>
                  <Box>
                    <PartnerFacilitySelector
                      data={partnerFacilityFilters}
                      value={selectedFacilities}
                      onChange={handleFacilityChange}
                      showPartnerCheckbox={true}
                      singleFacilitySelection={false}
                    />
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Box>
                    <Box className="flex-between-center pt-24">
                      <Box>
                        <CustomTypography
                          id="facility-list-status-filter-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          STATUS
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box className="pt-8">
                      <StatusFilterSelector
                        data={PartnerFacilityFilterData}
                        value={filterStatusId}
                        isForConsult={true}
                        onChange={handleStatusFilter}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box className="flex-between-center pt-24">
                      <Box>
                        <CustomTypography
                          id="facility-list-follow-up-filter-title"
                          variant="body2"
                          color="textDisabled"
                        >
                          FOLLOW-UP
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box>
                      <FollowUpFilterSelector
                        followUpdata={PartnerFacilityFilterData}
                        value={filterFollowUpId}
                        onChange={handleFollowUpFilter}
                      />
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
            <Box>
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <Box className="display-flex justify-end">
                    <Box className="pr-12">
                      <CustomButton
                        id="cancel-facility-user-button"
                        onClick={() => {
                          setAnchorEl(null);
                          setSelectedStatusId([]);
                          setSelectedFacilityIds([]);
                          setSelectedFollowUpId([]);
                          setFilterStatusId([]);
                          setFilterFollowUpId([]);
                          setSelectedFacilities({});
                          setFilterContext({
                            ...filterContext,
                            facilities: {},
                            statusIds: [],
                            followUpIds: [],
                          });
                        }}
                        variant="outlined"
                      >
                        Clear
                      </CustomButton>
                    </Box>
                    <Box>
                      <CustomButton
                        id="apply-facility-user-filter-button"
                        onClick={() => {
                          setAnchorEl(null);
                          handleApplyFilters();
                        }}
                        variant="contained"
                      >
                        Apply
                      </CustomButton>
                    </Box>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </Box>
        </CustomPopover>
      </Box>
    </Box>
  );
};

export default ConsultsList;
